// ============================================================================
// VINScanner.jsx - VIN Barcode Scanner (Using BarcodeDetector API)
// ============================================================================
// Scans VIN barcode from the door jamb sticker (Code 39, Code 128, Data Matrix)
// Much faster and more reliable than the previous OCR approach
// ============================================================================

import React, { Component } from 'react';
import BarcodeScanner from '../../common/BarcodeScanner';

class VINScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      detectedVIN: null,
      scannerActive: true,
      error: null
    };
  }

  handleBarcodeDetected = (rawValue, format) => {
    const value = rawValue.toUpperCase().trim();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(value)) return; // Not a valid VIN, keep scanning
    this.setState({ detectedVIN: value, scannerActive: false });
  };

  handleConfirm = () => {
    if (this.state.detectedVIN) {
      this.props.onVINScanned(this.state.detectedVIN);
    }
  };

  handleRetake = () => {
    this.setState({
      detectedVIN: null,
      scannerActive: true,
      error: null
    });
  };

  handleClose = () => {
    this.props.onClose();
  };

  render() {
    const { detectedVIN, scannerActive, error } = this.state;

    // Error state
    if (error) {
      return (
        <div style={styles.container}>
          <div style={styles.errorContainer}>
            <div className="alert alert-danger">
              <h4>Camera Error</h4>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={this.handleClose}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      );
    }

    // VIN detected — show confirmation
    if (detectedVIN) {
      return (
        <div style={styles.container}>
          <div style={styles.resultsContainer}>
            <div className="alert alert-success" role="alert">
              <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
              VIN detected successfully!
            </div>

            <div style={styles.vinBox}>
              <label style={styles.vinLabel}>Detected VIN:</label>
              <div style={styles.vinText}>{detectedVIN}</div>
            </div>

            <div style={styles.buttonGroup}>
              <button
                className="btn btn-success btn-lg btn-block"
                onClick={this.handleConfirm}
              >
                <i className="fa-sharp fa-regular fa-check mr-2"></i>
                Confirm VIN
              </button>

              <button
                className="btn btn-outline-secondary btn-block"
                onClick={this.handleRetake}
              >
                <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
                Scan Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Active scanning
    return (
      <div style={styles.container}>
        <BarcodeScanner
          formats={['code_39', 'code_128', 'data_matrix']}
          onDetected={this.handleBarcodeDetected}
          onError={(msg) => this.setState({ error: msg })}
          active={scannerActive}
          throttleMs={150}
        />

        {/* Instructions Overlay */}
        <div style={styles.instructionsOverlay}>
          <div style={styles.instructionsBox}>
            <strong>
              <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
              Point camera at VIN barcode
            </strong>
            <p style={styles.instructionsText}>
              Look for the barcode on the driver's door jamb sticker
            </p>
          </div>
        </div>

        {/* Cancel button */}
        <div style={styles.cameraControls}>
          <button
            className="btn btn-secondary btn-lg"
            onClick={this.handleClose}
          >
            <i className="fa-sharp fa-regular fa-times mr-2"></i>
            CANCEL
          </button>
        </div>
      </div>
    );
  }
}

// Styles
const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--color-bg-inverse)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column'
  },
  instructionsOverlay: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000,
    pointerEvents: 'none',
    maxWidth: '90%',
    width: '400px'
  },
  instructionsBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: '15px 20px',
    borderRadius: '8px',
    border: '2px solid var(--color-info)',
    color: 'var(--color-text-inverse)',
    textAlign: 'left'
  },
  instructionsText: {
    fontSize: '14px',
    marginBottom: 0,
    marginTop: '10px'
  },
  resultsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'var(--color-bg-card)',
    padding: '20px',
    maxHeight: '80%',
    overflowY: 'auto'
  },
  vinBox: {
    backgroundColor: 'var(--color-bg-panel)',
    padding: '20px',
    borderRadius: '8px',
    border: '2px solid var(--color-success)',
    marginBottom: '20px'
  },
  vinLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'var(--color-text-secondary)',
    marginBottom: '10px',
    display: 'block'
  },
  vinText: {
    fontSize: '24px',
    fontFamily: 'monospace',
    fontWeight: 'bold',
    color: 'var(--color-text-primary)',
    letterSpacing: '3px',
    textAlign: 'center'
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  cameraControls: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10000
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: '20px'
  }
};

export default VINScanner;
